import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { getOneReportMedia } from '@brands/services/oneReport/getOneReportMedia';
import { RegisterMediaPayload, registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { addOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/addOralHealthCoachingReportObject';
import { deleteOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/deleteOralHealthCoachingReportObject';
// `ICase` types its Oral Health Coaching report from this module, so the
// attachment type has to come from here too rather than from the
// same-named copy under services/oralHealthCoachingReport.
import { OralHealthCoachingReportMedia } from '@brands/services/careCoordinationReport/types/types';
import { updateOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/updateOralHealthCoachingReportObject';
import { CASE_ATTACHMENT_ACCEPT, convertHeicToJpeg, getUploadFileMimeType } from '@brands/Utils/caseAttachmentFiles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatDate } from '@brands/Utils/formatDate';
import { truncateFilename } from '@brands/Utils/truncateString';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, IconButton } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Loading } from '../../../Components/LoadingSpinner/Loading';
import ConfirmActionModal from '../Modals/ConfirmActionModal/ConfirmActionModal';
import styles from '../styles.module.scss';

type Props = {
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  userInfo: UserProfile;
  allowAttachmentUpdate: boolean;
};

/** An attachment joined to the resolved download URL for its media row. */
type ResolvedAttachment = OralHealthCoachingReportMedia & {
  url: string;
  filename: string;
  mimetype: string;
};

const isDisplayableImage = (mimetype?: string): boolean => (mimetype || '').toLowerCase().startsWith('image/');

/**
 * The report's object endpoints exist only under the care advisor,
 * collector and provider prefixes; there is no admin-scoped route, and
 * the shared helper falls admin roles back to `/care_advisor`, which
 * rejects them. Admins can see attachments here but not change them —
 * otherwise Add uploads and confirms a media row before the attach call
 * fails, orphaning it.
 */
const ROLES_THAT_CAN_EDIT_ATTACHMENTS: ReadonlySet<string> = new Set([
  UserRoleName.CareAdvisor,
  UserRoleName.Collector,
  UserRoleName.Provider,
]);

/**
 * Attachments on an Oral Health Coaching case.
 *
 * Separate from `AttachmentsCard`, which hangs everything off
 * `one_report` — a relation Oral Health Coaching cases do not have, so
 * that card's Add button silently did nothing here. These attachments
 * also carry a rule the clinical ones do not: each is internal to the
 * care team until the coordinator shares it onto the patient's report.
 */
const OralHealthCoachingAttachmentsCard = ({
  thisCase,
  setCase,
  userInfo,
  allowAttachmentUpdate,
}: Props): JSX.Element => {
  const [attachments, setAttachments] = useState<ResolvedAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<ResolvedAttachment | null>(null);

  const reportId = thisCase?.oral_health_coaching_report?.id;
  const media = thisCase?.oral_health_coaching_report?.media;
  const canEdit = allowAttachmentUpdate && Boolean(reportId) && ROLES_THAT_CAN_EDIT_ATTACHMENTS.has(userInfo.role.name);

  const resolveAttachments = useCallback(async (): Promise<void> => {
    if (!media || media.length === 0) {
      setAttachments([]);
      return;
    }
    setLoading(true);
    try {
      // Resolve each media row on its own: one dead reference should drop
      // that attachment, not blank the whole list.
      const resolved = await Promise.all(
        media.map(async (attachment): Promise<ResolvedAttachment | null> => {
          try {
            const file = await getOneReportMedia(attachment.media_id, userInfo.role.name);
            return { ...attachment, url: file.url, filename: file.filename, mimetype: file.mimetype };
          } catch (error) {
            console.error(`Failed to load Oral Health Coaching attachment ${attachment.media_id}:`, error);
            return null;
          }
        }),
      );
      setAttachments(
        resolved
          .filter((attachment): attachment is ResolvedAttachment => attachment !== null)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
      );
    } finally {
      setLoading(false);
    }
  }, [media, userInfo.role.name]);

  useEffect(() => {
    resolveAttachments();
  }, [resolveAttachments]);

  /**
   * Re-read the case rather than trusting the mutation's return value:
   * the download URLs are rebuilt from `media`, and the case is what the
   * rest of the consultation page renders from.
   */
  const refreshCase = async (): Promise<void> => {
    const currentCase = await getOneCase(Number(thisCase.id), undefined, userInfo.role.name);
    setCase((previous) => ({
      ...previous,
      oral_health_coaching_report: currentCase.oral_health_coaching_report,
      updated_at: currentCase.updated_at,
    }));
  };

  const uploadFile = async (pickedFile: File): Promise<void> => {
    // Convert before registering, so a failed conversion leaves no
    // orphaned media row behind.
    const file = await convertHeicToJpeg(pickedFile);
    const mimetype = getUploadFileMimeType(file);
    const payload: RegisterMediaPayload = { files: [{ filename: file.name, mimetype }] };

    const registered = await registerOneReportMedia(payload, userInfo.role.name);
    const mediaId = registered.files[0].upload_id;
    await fetch(registered.files[0].upload_url, {
      method: 'PUT',
      body: file,
      headers: { 'content-type': mimetype },
    });
    await confirmOneReportMedia(mediaId, userInfo.role.name);
    await addOralHealthCoachingReportObject(
      String(reportId),
      'media',
      { media_id: mediaId, note: '' },
      userInfo.role.name,
    );
  };

  const addAttachments = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files || []);
    // Clear immediately so re-picking the same file still fires onChange.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (files.length === 0) return;

    if (!reportId) {
      toast.error('This case has no Oral Health Coaching report yet, so attachments cannot be added.', {
        className: 'fs-unmask',
      });
      return;
    }

    setBusy(true);
    try {
      // Sequential: every upload appends to the same report.
      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await uploadFile(file);
      }
      await refreshCase();
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleShared = async (attachment: ResolvedAttachment): Promise<void> => {
    if (!canEdit) return;
    setBusy(true);
    try {
      await updateOralHealthCoachingReportObject(
        String(reportId),
        'media',
        String(attachment.id),
        { is_selected: !attachment.is_selected },
        userInfo.role.name,
      );
      await refreshCase();
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Removal is permanent and these are the care team's own documents, so
   * it goes through the same confirm-before-delete step the One Report
   * attachments use.
   */
  const removeAttachment = async (): Promise<void> => {
    if (!canEdit || !pendingRemoval) return;
    setBusy(true);
    try {
      await deleteOralHealthCoachingReportObject(
        String(reportId),
        String(pendingRemoval.id),
        'media',
        userInfo.role.name,
      );
      await refreshCase();
      setPendingRemoval(null);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.consultationPageRightSideFrameCaseDetails}>
      <div className={styles.consultationPageRightSideFrameCaseDetailsTitle}>
        <div className={styles.consultationPageRightSideFrameCaseDetailsTitleDiv}>
          <span>Attachments</span>
          <Button
            className={styles.addPhotos}
            variant="text"
            component="label"
            startIcon={<AddIcon />}
            disabled={!canEdit || busy}
          >
            Add
            <input
              type="file"
              hidden
              multiple
              accept={CASE_ATTACHMENT_ACCEPT}
              onChange={addAttachments}
              disabled={!canEdit || busy}
            />
          </Button>
        </div>
      </div>

      <span className="fs-unmask">
        Attachments are visible to the care team only. Use Share to add one to the patient&apos;s report.
      </span>

      {(loading || busy) && <Loading dotted />}

      {attachments.length === 0 && !loading && !busy && <span className="fs-unmask">No attachments yet.</span>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '12px',
          alignItems: 'start',
          width: '100%',
        }}
      >
        {attachments.map((attachment) => (
          <div
            className={`fs-exclude ${styles.imageContainer}`}
            key={attachment.id}
            style={{ width: '100%', margin: 0 }}
          >
            <div className={`${styles.imageCard} ${attachment.is_selected ? styles.selectedCard : ''}`}>
              {isDisplayableImage(attachment.mimetype) ? (
                <img src={attachment.url} alt={attachment.filename} className={styles.image} loading="lazy" />
              ) : (
                <div className={styles.fileSquareContent}>
                  <InsertDriveFileIcon sx={{ fontSize: 50 }} />
                  <span className={styles.filename}>{truncateFilename(attachment.filename, 20)}</span>
                </div>
              )}

              <div className={styles.attachmentOverlayActions}>
                <IconButton
                  size="small"
                  title="Open"
                  onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>

                {canEdit && (
                  <IconButton size="small" title="Remove" onClick={() => setPendingRemoval(attachment)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </div>

              <div className={styles.imageDate}>{formatDate(attachment.created_at)}</div>
            </div>

            <Button
              variant="text"
              size="small"
              className="fs-unmask"
              disabled={!canEdit || busy}
              onClick={() => toggleShared(attachment)}
            >
              {attachment.is_selected ? 'Shared — unshare' : 'Share with patient'}
            </Button>
          </div>
        ))}
      </div>

      <ConfirmActionModal
        open={Boolean(pendingRemoval)}
        message={`Are you sure you want to remove ${pendingRemoval ? pendingRemoval.filename : 'this attachment'}?`}
        isLoading={busy}
        onClose={() => setPendingRemoval(null)}
        onConfirm={removeAttachment}
      />
    </div>
  );
};

export default OralHealthCoachingAttachmentsCard;
